import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';

import { Button } from '@/components/ButtonComponent';
import { RadioItem } from '@/components/RadioItem';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { KycProvider } from '@/feature/kyc/const';
import { useNavigateToDeposit } from '@/feature/kyc/hooks/use-navigate-to-deposit';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { mapProductToDefaultVertical, useUpdateUserSettings } from '@/hooks/use-auth-user-settings';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { GlobalConfigKeys, Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { usePromoCodeDefaultProductSyncMap } from '@/hooks/use-promo-code-default-product-sync';
import { useUserStore } from '@/hooks/use-user-store';
import { KycStackScreenProps } from '@/navigation/kyc/types';
import { common } from '@/styles/styles';
import { readDeeplinkReferredProduct } from '@/utils/deeplink-referred-product';
import { logger } from '@/utils/logging';
import { mapPromoCodeToProduct } from '@/utils/promo-referred-product';

type ScreenProps = KycStackScreenProps<'DefaultProductOnboarding'>;

export const DefaultProductOnboarding = ({ navigation, route }: ScreenProps) => {
    const { kycProvider } = route.params;
    const { t } = useTranslation(['product_switch_menu', 'account', 'common']);

    usePreventAndroidBackNavigation();

    const { availableProducts } = useAvailableProducts();
    const { mutate: updateUserSettings } = useUpdateUserSettings();
    const setProduct = useJurisdictionStore(state => state.actions.setProduct);
    const jurisdictionDefaultProduct = useJurisdictionStore(state => state.jurisdictionSettings?.productType ?? null);
    const globalSettings = useJurisdictionStore(state => state.jurisdictionSettings?.globalSettings);
    const productDescriptions = globalSettings?.keyValuePairs?.[GlobalConfigKeys.DefaultProductOnboardingDescription];
    const { syncMap: promoSyncMap, isLoading: isPromoSyncMapLoading } = usePromoCodeDefaultProductSyncMap();
    const preselectJurisdictionDefaultProduct =
        globalSettings?.featureFlags?.enable_jurisdiction_default_for_default_product_onboarding?.enabled ?? false;
    const navigateToDeposit = useNavigateToDeposit();

    // The promo code the user registered with, decoded from the Keycloak JWT, resolved to a default
    // product via the Prismic promo_code_default_product_sync map.
    const profilePromoCode = useUserStore(state => state.profile?.promo_code);
    const promoProduct = profilePromoCode ? mapPromoCodeToProduct(profilePromoCode, promoSyncMap) : null;

    const [deeplinkProduct, setDeeplinkProduct] = useState<Product | null>(null);
    const [pendingProduct, setPendingProduct] = useState<Product | null>(null);

    useEffect(() => {
        readDeeplinkReferredProduct().then(setDeeplinkProduct);
    }, []);

    // Preselect the promo-code product, then the deep link product, then the jurisdiction default (if enabled).
    // With no match the user must pick manually. A manual tap always wins. While the promo sync map is still
    // loading for a user with a promo code, hold the preselection so the radio doesn't flash to a fallback product.
    const selectableProducts = availableProducts.filter(product => product !== Product.None);

    const isPromoProductPending = !!profilePromoCode && isPromoSyncMapLoading;

    const preselectedProduct = isPromoProductPending
        ? null
        : [promoProduct, deeplinkProduct, preselectJurisdictionDefaultProduct ? jurisdictionDefaultProduct : null].find(
              product => product && product !== Product.None && selectableProducts.includes(product)
          ) ?? null;

    const selectedProduct = pendingProduct ?? preselectedProduct;

    const proceedWithProduct = useCallback(
        (product: Product) => {
            const selectedVertical = mapProductToDefaultVertical(product);
            if (selectedVertical) {
                updateUserSettings(
                    { default_vertical: selectedVertical },
                    {
                        onError: error =>
                            logger.error(
                                '[DefaultProductOnboarding]',
                                "Failed to update User's default vertical",
                                error
                            ),
                    }
                );
            }

            if (kycProvider === KycProvider.TRUSTLY) {
                setProduct(product);
                const isCoinProduct = product === Product.Sweeps || product === Product.Casino;
                if (isCoinProduct) {
                    navigation.navigate('DepositStack', {
                        screen: 'CoinPackageScreen',
                        params: { isTrustlyKyc: true },
                    });
                } else {
                    navigation.navigate('TrustlyNumpad');
                }
                return;
            }

            navigateToDeposit(product);
        },
        [updateUserSettings, kycProvider, setProduct, navigation, navigateToDeposit]
    );

    const handleNext = useCallback(() => {
        if (selectedProduct) {
            proceedWithProduct(selectedProduct);
        }
    }, [selectedProduct, proceedWithProduct]);

    return (
        <Screen edges={['top', 'bottom']}>
            <ScreenNavBar closeIconMode={'none'} title={t('common:default_product')} />
            <ScrollView>
                <MaxWidthWrapper paddingHorizontal={'s16'} paddingBottom={'s20'}>
                    <SizedBox value={8} />
                    <Text variant="headlineMedium">{t('account:default_product_onboarding_title')}</Text>
                    <SizedBox value={8} />
                    <Text variant="bodySmall" color="neutral300">
                        {t('account:default_product_onboarding_subtitle')}
                    </Text>
                    <SizedBox value={32} />
                    <Box gap={'s12'}>
                        {availableProducts.map(product => {
                            if (product === Product.None) {
                                return null;
                            }

                            const isSelected = selectedProduct === product;
                            const description = productDescriptions?.[product];

                            return (
                                <TouchableBox
                                    key={product}
                                    padding={'s16'}
                                    borderRadius={'r16'}
                                    borderWidth={1}
                                    gap={'s12'}
                                    flexDirection={'row'}
                                    alignItems={'center'}
                                    backgroundColor={ds.colorToken.background.neutral.subtler}
                                    borderColor={
                                        isSelected
                                            ? ds.colorToken.background.neutral.inverse
                                            : ds.colorToken.border.strong
                                    }
                                    style={common.borderCurve}
                                    onPress={() => setPendingProduct(product)}
                                >
                                    <Box flex={1}>
                                        <Text variant="titleMedium" color={ds.colorToken.text.primary}>
                                            {t(product)}
                                        </Text>
                                        {description ? (
                                            <>
                                                <SizedBox value={4} />
                                                <Text variant="bodySmall" color="neutral300">
                                                    {description}
                                                </Text>
                                            </>
                                        ) : null}
                                    </Box>
                                    <RadioItem selected={isSelected} />
                                </TouchableBox>
                            );
                        })}
                    </Box>
                </MaxWidthWrapper>
            </ScrollView>
            <MaxWidthWrapper>
                <Box px={'s16'} pt={'s12'} pb={'s16'}>
                    <Button
                        label={t('common:next')}
                        hierarchy={'primary'}
                        onPress={handleNext}
                        disabled={!selectedProduct}
                    />
                </Box>
            </MaxWidthWrapper>
        </Screen>
    );
};
